import {
  Avatar,
  Button,
  cn,
  Menu,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  MenuToggle,
  StatusDot,
} from "@kontaktfix/ui";
import {
  AddressBookIcon,
  ArrowsClockwiseIcon,
  BellIcon,
  BookOpenTextIcon,
  BuildingsIcon,
  CaretUpDownIcon,
  GaugeIcon,
  GearIcon,
  ListChecksIcon,
  ListIcon,
  MoonIcon,
  PauseCircleIcon,
  PulseIcon,
  SignOutIcon,
  TranslateIcon,
  UploadSimpleIcon,
  UsersIcon,
  XIcon,
} from "@phosphor-icons/react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BillingBanner } from "@/components/billing-banner";
import { BugReportButton } from "@/components/bug-report";
import { Logo } from "@/components/logo";
import { api } from "@/lib/api";
import { daysUntil, setLanguage } from "@/lib/i18n";
import { useMe, useSyncOverview } from "@/lib/queries";
import { applyTheme, getTheme } from "@/lib/theme";

/** The manual on the public website (same pages for every environment). */
const MANUAL_URL = { de: "https://kontaktfix.ch/handbuch", en: "https://kontaktfix.ch/en/handbook" } as const;

type NavItem = { to: string; label: string; icon: ReactNode; exact?: boolean };

function NavLink({ item, onNavigate }: { item: NavItem; onNavigate: () => void }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const active = item.exact
    ? pathname === item.to
    : pathname === item.to || pathname.startsWith(`${item.to}/`);
  return (
    <Link
      to={item.to}
      onClick={onNavigate}
      className={cn(
        "flex h-8 items-center gap-2.5 rounded-md px-2.5 text-[13px] font-medium transition-colors [&_svg]:size-4",
        active ? "bg-muted text-fg" : "text-fg-muted hover:bg-muted/60 hover:text-fg",
      )}
    >
      {item.icon}
      {item.label}
    </Link>
  );
}

function SyncIndicator() {
  const { t } = useTranslation();
  const { data } = useSyncOverview();
  if (!data) return null;
  const problems = data.totals.error + data.totals.unreachable;
  return (
    <Link
      to="/sync"
      className="flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[12px] text-fg-muted hover:bg-muted/60"
    >
      <StatusDot tone={data.running ? "accent" : problems ? "warning" : "success"} pulse={data.running} />
      {data.running
        ? t("sync.inProgress")
        : problems
          ? t("sync.attention", { count: problems })
          : t("sync.upToDate")}
    </Link>
  );
}

/** Kontaktfix suspended the organization: members cannot work in it until it is resumed. */
function SuspendedNotice({ orgName, onLogout }: { orgName: string; onLogout: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-screen items-center justify-center bg-subtle px-4">
      <div className="w-full max-w-md rounded-lg border border-border bg-bg p-6 text-center shadow-md">
        <div className="mx-auto mb-4 flex size-10 items-center justify-center rounded-lg bg-warning-soft text-warning">
          <PauseCircleIcon className="size-5" />
        </div>
        <h1 className="text-[16px] font-semibold">{t("suspended.title", { org: orgName })}</h1>
        <p className="mt-2 text-[13px] text-fg-muted">{t("suspended.text")}</p>
        <Button className="mt-5" onClick={onLogout}>
          <SignOutIcon />
          {t("nav.logout")}
        </Button>
      </div>
    </div>
  );
}

export function AppShell() {
  const { t, i18n } = useTranslation();
  const { data: me } = useMe();
  const navigate = useNavigate();
  const client = useQueryClient();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [theme, setThemeState] = useState(getTheme());
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Close the mobile menu after navigating.
  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname is the trigger
  useEffect(() => setMobileOpen(false), [pathname]);

  if (!me) return null;
  const isAdmin = me.role === "admin";

  const nav: NavItem[] = [
    { to: "/", label: t("nav.overview"), icon: <GaugeIcon />, exact: true },
    { to: "/contacts", label: t("nav.contacts"), icon: <AddressBookIcon /> },
    { to: "/lists", label: t("nav.lists"), icon: <ListChecksIcon /> },
    { to: "/people", label: t("nav.people"), icon: <UsersIcon /> },
    { to: "/sync", label: t("nav.sync"), icon: <ArrowsClockwiseIcon /> },
    { to: "/activity", label: t("nav.activity"), icon: <PulseIcon /> },
  ];
  const secondary: NavItem[] = [
    { to: "/import", label: t("nav.import"), icon: <UploadSimpleIcon /> },
    ...(isAdmin ? [{ to: "/settings", label: t("nav.settings"), icon: <GearIcon /> }] : []),
  ];

  const logout = async () => {
    await api.post("/api/auth/logout");
    client.clear();
    await navigate({ to: "/login" });
  };
  const exitSupport = async () => {
    const { redirect } = await api.post<{ redirect: string }>("/api/support/exit");
    client.clear();
    // Back to the operator dashboard with a full load (its own session).
    window.location.assign(redirect);
  };
  const toggleTheme = (dark: boolean) => {
    const next = dark ? "dark" : "light";
    applyTheme(next);
    setThemeState(next);
  };

  if (me.org?.suspended && !me.supportMode)
    return <SuspendedNotice orgName={me.org.name} onLogout={logout} />;

  const sidebar = (
    <div className="flex h-full flex-col">
      <div className="flex h-14 items-center justify-between px-4">
        <Link to="/">
          <Logo />
        </Link>
        <button
          type="button"
          className="rounded-md p-1 text-fg-muted lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-label={t("common.close")}
        >
          <XIcon className="size-5" />
        </button>
      </div>
      {me.org && (
        <div className="mx-3 mb-3 flex items-center gap-2 rounded-md border border-border bg-bg px-2.5 py-2 shadow-sm">
          <span className="flex size-6 shrink-0 items-center justify-center rounded bg-muted text-fg-muted">
            <BuildingsIcon className="size-3.5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[12.5px] font-medium">{me.org.name}</div>
            <div className="truncate text-[11.5px] text-fg-subtle">
              {me.role ? t(`roles.${me.role}`) : ""}
            </div>
          </div>
        </div>
      )}
      {me.org?.billing.state === "trial" && isAdmin && (
        <Link
          to="/settings/{-$tab}"
          params={{ tab: "billing" }}
          className="mx-3 mb-3 -mt-1.5 block rounded-md px-2.5 py-1 text-[11.5px] text-fg-muted hover:bg-muted/60"
        >
          {t("billing.sidebarTrial", { count: daysUntil(me.org.billing.trialEndsAt) })}
        </Link>
      )}
      <nav className="flex flex-col gap-0.5 px-3">
        {nav.map((item) => (
          <NavLink key={item.to} item={item} onNavigate={() => setMobileOpen(false)} />
        ))}
      </nav>
      <div className="mx-5 my-3 h-px bg-border" />
      <nav className="flex flex-col gap-0.5 px-3">
        {secondary.map((item) => (
          <NavLink key={item.to} item={item} onNavigate={() => setMobileOpen(false)} />
        ))}
      </nav>
      <div className="mt-auto flex flex-col gap-2 p-3">
        <BugReportButton endpoint="/api/bug-reports" />
        <SyncIndicator />
        <Menu
          align="start"
          trigger={
            <button
              type="button"
              className="flex w-full items-center gap-2.5 rounded-md p-2 text-left hover:bg-muted/60"
            >
              <Avatar name={me.identity.name} size={28} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[12.5px] font-medium">{me.identity.name}</div>
                <div className="truncate text-[11.5px] text-fg-subtle">{me.identity.email}</div>
              </div>
              <CaretUpDownIcon className="size-4 text-fg-subtle" />
            </button>
          }
        >
          <MenuLabel>{me.identity.email}</MenuLabel>
          <MenuItem icon={<BellIcon />} onSelect={() => navigate({ to: "/account/notifications" })}>
            {t("notifications.title")}
          </MenuItem>
          <MenuItem
            icon={<BookOpenTextIcon />}
            onSelect={() =>
              window.open(MANUAL_URL[i18n.language === "en" ? "en" : "de"], "_blank", "noopener")
            }
          >
            {t("nav.handbook")}
          </MenuItem>
          <MenuToggle checked={theme === "dark"} onChange={toggleTheme} icon={<MoonIcon />}>
            {t("nav.theme")}
          </MenuToggle>
          <MenuSeparator />
          <MenuLabel>
            <span className="flex items-center gap-1.5">
              <TranslateIcon className="size-3.5" />
              {t("nav.language")}
            </span>
          </MenuLabel>
          <MenuItem onSelect={() => setLanguage("de")}>{i18n.language === "de" ? "✓ " : ""}Deutsch</MenuItem>
          <MenuItem onSelect={() => setLanguage("en")}>{i18n.language === "en" ? "✓ " : ""}English</MenuItem>
          <MenuSeparator />
          <MenuItem icon={<SignOutIcon />} onSelect={logout}>
            {t("nav.logout")}
          </MenuItem>
        </Menu>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-bg">
      <aside className="sticky top-0 hidden h-screen w-60 shrink-0 border-r border-border bg-subtle lg:block">
        {sidebar}
      </aside>
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/30" onClick={() => setMobileOpen(false)} aria-hidden />
          <aside className="animate-fade-in absolute inset-y-0 left-0 w-72 border-r border-border bg-subtle">
            {sidebar}
          </aside>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        {me.supportMode && (
          <div className="flex flex-wrap items-center justify-center gap-3 bg-warning-soft px-4 py-2 text-[12.5px] text-warning">
            <span>{t("nav.support", { org: me.org?.name })}</span>
            <Button size="sm" variant="secondary" onClick={exitSupport}>
              {t("nav.exitSupport")}
            </Button>
          </div>
        )}
        {me.partner && (
          <div className="flex flex-wrap items-center justify-center gap-3 bg-accent-soft px-4 py-2 text-[12.5px] text-accent">
            <span>{t("partner.banner.working", { org: me.org?.name, partner: me.partner.name })}</span>
            <Button asChild size="sm" variant="secondary">
              <a href="/partner/customers">{t("partner.banner.switch")}</a>
            </Button>
          </div>
        )}
        <BillingBanner me={me} />
        <header className="flex h-14 items-center gap-3 border-b border-border px-4 lg:hidden">
          <button
            type="button"
            className="rounded-md p-1 text-fg-muted"
            onClick={() => setMobileOpen(true)}
            aria-label="Menu"
          >
            <ListIcon className="size-5" />
          </button>
          <Logo />
        </header>
        <main className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
